<template>
    <BaseModal :title="token ? $t('titleEdit') : $t('title')" v-on="$listeners">
        <BaseCard padded class="l-stack l-gap-2">
            <div>
                <p class="form-label">
                    {{ $t('description') }}
                </p>
                <BaseInput
                    v-model="description"
                    :placeholder="$t('description')"
                />
            </div>
            <div>
                <p class="form-label">
                    {{ $t('shared.assets') }}
                </p>
                <BaseMultiselect
                    v-model="assets"
                    :placeholder="$t('shared.assets')"
                    :options="trackers"
                    track-by="id"
                    :custom-label="option => option.asset_details.name"
                    multiple
                />
            </div>
            <div>
                <p class="form-label">
                    {{ $t('validFrom') }}
                </p>
                <DateInput
                    v-model="validFrom"
                    :placeholder="$t('validFrom')"
                    :disabled-dates="{ from: validTo }"
                />
            </div>
            <div>
                <p class="form-label">
                    {{ $t('validTo') }}
                </p>
                <DateInput
                    v-model="validTo"
                    :placeholder="$t('validTo')"
                    :disabled-dates="{ to: validFrom }"
                />
            </div>
        </BaseCard>

        <template #footer>
            <div class="l-inline l-justify-end">
                <BaseButton
                    size="small"
                    :disabled="isDisabled"
                    :is-loading="isLoading"
                    @click="handleSubmit"
                >
                    {{ token ? $t('submitEdit') : $t('submit') }}
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'

import { httpHelper } from '@/utils'
import BaseButton from './BaseButton'
import BaseCard from './BaseCard'
import BaseInput from './BaseInput'
import BaseModal from './BaseModal'
import BaseMultiselect from './BaseMultiselect'
import DateInput from './DateInput'

export default {
    name: 'SharingTokenEditModal',
    components: {
        BaseButton,
        BaseCard,
        BaseInput,
        BaseModal,
        BaseMultiselect,
        DateInput,
    },
    props: {
        onSubmit: {
            type: Function,
            default: null,
        },
        token: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            assets: [],
            description: '',
            isLoading: false,
            validFrom: moment
                .utc()
                .startOf('day')
                .toDate(),
            validTo: moment
                .utc()
                .endOf('day')
                .toDate(),
        }
    },
    computed: {
        ...mapState('tracker', ['trackers']),
        ...mapGetters('tracker', ['assetsByAssetId']),
        isDisabled() {
            return !this.assets.length || !this.validFrom || !this.validTo
        },
    },
    watch: {
        token: {
            immediate: true,
            handler() {
                this.assets =
                    this.token?.assets.map(
                        asset => this.assetsByAssetId[asset]
                    ) || []
                this.description = this.token?.description ?? ''
                this.validFrom = this.token
                    ? new Date(this.token.valid_from)
                    : moment
                          .utc()
                          .startOf('day')
                          .toDate()
                this.validTo = this.token
                    ? new Date(this.token.valid_to)
                    : moment
                          .utc()
                          .endOf('day')
                          .toDate()
            },
        },
    },
    methods: {
        async handleSubmit() {
            try {
                this.isLoading = true
                const data = {
                    assets: this.assets.map(({ asset }) => asset),
                    description: this.description,
                    valid_from: moment(this.validFrom)
                        .startOf('day')
                        .toDate(),
                    valid_to: moment(this.validTo)
                        .endOf('day')
                        .toDate(),
                }
                if (this.token) {
                    await httpHelper.patch(
                        `/share/tokens/${this.token.id}/`,
                        data
                    )
                } else {
                    await httpHelper.post('/share/tokens/', data)
                }
                this.onSubmit?.()
                this.$emit('close')
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "description": "Description",
        "submit": "Create",
        "submitEdit": "Apply",
        "title": "Create sharing token",
        "titleEdit": "Edit sharing token",
        "validFrom": "Valid from",
        "validTo": "Valid to"
    },
    "de": {
        "description": "Beschreibung",
        "submit": "Erstellen",
        "submitEdit": "Anwenden",
        "title": "Neuer Freigabetoken",
        "titleEdit": "Freigabetoken bearbeiten",
        "validFrom": "Gültig ab",
        "validTo": "Gültig bis"
    },
    "fr": {
        "description": "Description",
        "submit": "Créer",
        "submitEdit": "Appliquer",
        "title": "Nouveau token de partage",
        "titleEdit": "Modifier un token de partage",
        "validFrom": "Valide à partir du",
        "validTo": "Valide jusqu’au"
    },
    "it": {
        "description": "Descrizione",
        "submit": "Creare",
        "submitEdit": "Applicare",
        "title": "Nuovo token di condivisione",
        "titleEdit": "Modifica un token di condivisione",
        "validFrom": "Valido dal",
        "validTo": "Valido fino al"
    }
}
</i18n>

<style lang="scss" scoped>
.sharing-token-edit-modal {
    &__card {
        gap: 16px;
    }
}
</style>
